<template>
  <!-- Autocomplete de référence unique : suggestions servies par l'API, restreintes
       aux références en stock dans l'entrepôt demandé (INC-007). La saisie libre
       d'un code interne n'est volontairement pas acceptée : la sélection garantit
       que la référence existe et est transférable. -->
  <v-select
    :value="value"
    :options="results"
    :filterable="false"
    :disabled="disabled"
    :placeholder="placeholder"
    label="internal_code"
    class="reference-ac"
    @input="onInput"
    @search="onSearch"
  >
    <template slot="option" slot-scope="opt">
      <div class="reference-ac__opt">
        {{ optionLabel(opt) }}
        <span v-if="!opt.warehouse_id" class="reference-ac__warning">— entrepôt non affecté</span>
      </div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="reference-ac__label">{{ optionLabel(opt) }}</span>
    </template>

    <template slot="no-options" slot-scope="{ search }">
      <div class="reference-ac__empty">
        <template v-if="!warehouseId">
          <i class="far fa-exclamation-triangle"></i> Sélectionnez d'abord l'entrepôt source.
        </template>
        <template v-else-if="search.trim().length < minChars">
          <i class="far fa-keyboard"></i> Tapez au moins {{ minChars }} caractères…
        </template>
        <template v-else-if="loading">Recherche…</template>
        <template v-else>
          Aucune référence en stock dans cet entrepôt pour «&nbsp;{{ search }}&nbsp;»
        </template>
      </div>
    </template>
  </v-select>
</template>

<script>
// Longueur minimale de recherche avant d'interroger le serveur.
const MIN_CHARS = 2;
// Délai avant l'appel serveur, à la frappe.
const DEBOUNCE = 300;
// Nombre max de suggestions demandées au serveur.
const LIMIT = 50;

export default {
  name: "ReferenceAutocomplete",
  props: {
    /** Référence sélectionnée : {id, internal_code, product_name, warehouse_id} ou null. */
    value: { type: Object, default: null },
    /** Entrepôt dans lequel la référence doit être en stock (null = pas de recherche). */
    warehouseId: { type: Number, default: null },
    placeholder: { type: String, default: "Code interne ou désignation…" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { results: [], loading: false, minChars: MIN_CHARS, timer: null };
  },
  watch: {
    // Changer d'entrepôt source invalide les suggestions déjà affichées.
    warehouseId() {
      this.results = [];
    },
  },
  mounted() {
    // Préremplissage (depuis une fiche référence) : la référence n'est connue que
    // par son id/code, on complète la désignation et l'entrepôt via l'API.
    if (this.value && this.value.id && !this.value.product_name) {
      this.resolveValue();
    }
  },
  methods: {
    optionLabel(reference) {
      if (!reference) return "";
      return reference.product_name
        ? reference.internal_code + " - " + reference.product_name
        : reference.internal_code;
    },
    onInput(reference) {
      this.$emit("input", reference || null);
    },
    onSearch(search, loading) {
      const q = (search || "").trim();
      clearTimeout(this.timer);

      if (!this.warehouseId || q.length < MIN_CHARS) {
        this.results = [];
        return;
      }

      this.timer = setTimeout(() => {
        if (loading) loading(true);
        this.loading = true;
        this.fetch(q)
          .then((references) => {
            this.results = references;
          })
          .catch(() => {
            this.results = [];
          })
          .then(() => {
            this.loading = false;
            if (loading) loading(false);
          });
      }, DEBOUNCE);
    },
    fetch(search) {
      return this.$http
        .get(process.env.BACK_API_URL + "reference/search/in-stock", {
          params: { search: search, warehouse: this.warehouseId, limit: LIMIT },
        })
        .then((response) => (response.data && response.data.references) || []);
    },
    // Complète la sélection initiale ; si la référence n'est pas en stock dans
    // l'entrepôt, elle reste affichée telle quelle et la validation la rejettera.
    resolveValue() {
      this.fetch(this.value.internal_code || "")
        .then((references) => {
          const match = references.find((r) => r.id === this.value.id);
          if (match) this.$emit("input", match);
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.reference-ac {
  width: 100%;
}
.reference-ac >>> .vs__dropdown-toggle {
  background: #fff;
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  max-width: 100%;
  padding: 0;
  align-items: stretch;
}
.reference-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.reference-ac >>> .vs__selected {
  font-size: 13px;
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
.reference-ac >>> .vs__selected .reference-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.reference-ac >>> .vs__search {
  font-size: 13px;
  margin: 0;
}
.reference-ac >>> .vs__actions {
  padding-top: 0;
}
.reference-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.reference-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.reference-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}
.reference-ac >>> .vs__dropdown-option--highlight .reference-ac__opt,
.reference-ac >>> .vs__dropdown-option--highlight .reference-ac__warning {
  color: #fff;
}
.reference-ac__opt {
  white-space: normal;
  color: #34495e;
  font-size: 13px;
}
.reference-ac__label {
  color: #34495e;
  font-size: 13px;
}
.reference-ac__warning {
  color: #f8ac59;
  font-size: 12px;
}
.reference-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/ReferenceAutocomplete.vue